import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActionSheetController, ModalController, Platform } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { ModalLocationPickerComponent } from '../modal-location-picker/modal-location-picker.component';
import { GoogleMapsService } from '../../services/google-maps.service';
import { ControllerService } from '../../services/controller.service';
import { Coordinate, Location } from '../../interfaces/location';
import { FormGroup } from '@angular/forms';
import { FormControlOption } from 'src/app/interfaces/form-control-option';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import { firstValueFrom } from 'rxjs';

type addressFormFields = 'full_address';

@Component({
  selector: 'app-location-picker',
  templateUrl: './location-picker.component.html',
  styleUrls: ['./location-picker.component.scss'],
})
export class LocationPickerComponent implements OnInit {
  @Input() location: Location;
  @Input() parentFormGroup: FormGroup;
  @Input() formControlOptions: Record<string, FormControlOption>;

  @Output() locationChange = new EventEmitter<Location>();

  // use alun-alun surabaya coordinate by default
  defaultLocation: Location = {
    latitude: -7.26363486322988,
    longitude: 112.74532594549336,
    googleMapsAddress: 'Jl. Gubernur Suryo No.15, Embong Kaliasin, Kec. Genteng, Kota SBY, Jawa Timur 21256',
    googleMapsSearchUrl: this.googleMapsService.getGoogleMapsSearchUrl(
      -7.26363486322988,
      112.74532594549336,
      'ChIJlYXv3iz51y0RLLhpJ0iVitM',
    ),
  };

  constructor(
    private actionSheetController: ActionSheetController,
    private modalController: ModalController,
    private googleMapsService: GoogleMapsService,
    private controllerService: ControllerService,
    private platform: Platform,
  ) {}

  ngOnInit() {}

  async onPick() {
    const actionSheet = await this.actionSheetController.create({
      header: 'Pilih Lokasi',
      mode: 'ios',
      buttons: [
        {
          text: 'Gunakan lokasi saat ini',
          handler: async () => {
            await actionSheet.dismiss();

            await this.useCurrentLocation();
          },
        },
        {
          text: 'Pilih di peta',
          handler: async () => {
            await actionSheet.dismiss();

            await this.chooseOnMap();
          },
        },
        {
          role: 'cancel',
          text: 'Batal',
        },
      ],
    });

    await actionSheet.present();
  }

  async openAppSettings() {
    if (this.platform.is('ios')) {
      await NativeSettings.openIOS({
        option: IOSSettings.App,
      });
    } else {
      await NativeSettings.openAndroid({
        option: AndroidSettings.ApplicationDetails,
      });
    }
  }

  async updateLocation(address: string) {
    if (!address) {
      return;
    }

    let location: Location;

    try {
      const res = await firstValueFrom(this.googleMapsService.geocoding(address));
      const result = res.results[0];
      const latitude = result?.geometry.location.lat;
      const longitude = result?.geometry.location.lng;

      location = {
        latitude,
        longitude,
        googleMapsAddress: address,
        googleMapsSearchUrl: this.googleMapsService.getGoogleMapsSearchUrl(latitude, longitude, result.place_id),
      };

      return location;
    } catch (error) {
      console.error('Error updating location:', error);
      return null;
    }
  }

  async useCurrentLocation() {
    if (!Capacitor.isPluginAvailable('Geolocation')) {
      await this.controllerService.presentAlert({
        header: 'Error',
        message: 'Device anda tidak mendukung fitur geolocation, harap pilih di peta!',
      });

      return;
    }

    await this.controllerService.presentLoading({
      message: 'Sedang mengambil posisi anda...',
    });

    try {
      const position = await Geolocation.getCurrentPosition({
        enableHighAccuracy: true,
      });

      this.location = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        googleMapsSearchUrl: this.googleMapsService.getGoogleMapsSearchUrl(
          position.coords.latitude,
          position.coords.longitude,
        ),
      };

      this.googleMapsService.reverseGeocoding(position.coords.latitude, position.coords.longitude).subscribe({
        next: async ({ results }) => {
          if (!results.length) {
            await this.controllerService.presentAlert({
              header: 'Info',
              message: 'Alamat tidak ditemukan pada google maps',
            });

            return;
          }

          const result = results[0];
          console.log(result);
          const kecamatan = result.address_components.find(item =>
            item.types.includes('administrative_area_level_3'),
          )?.long_name;
          const kab_kota = result.address_components.find(item =>
            item.types.includes('administrative_area_level_2'),
          )?.long_name;
          const provinsi = result.address_components.find(item =>
            item.types.includes('administrative_area_level_1'),
          )?.long_name;
          const postal_code = result.address_components.find(item => item.types.includes('postal_code'))?.long_name;

          this.location.googleMapsAddress = this.takeDetailAddress(result.formatted_address);
          this.location.kecamatan = kecamatan;
          this.location.kab_kota = kab_kota;
          this.location.provinsi = provinsi;
          this.location.postal_code = postal_code;

          this.locationChange.emit(this.location);

          await this.controllerService.dismissLoading();
        },
        error: async () => {
          await this.controllerService.presentAlert({
            header: 'Error',
            message: 'Terjadi kesalahan ketika mencari alamat pada google maps',
          });

          this.locationChange.emit(this.location);

          await this.controllerService.dismissLoading();
        },
      });
    } catch (error) {
      await this.controllerService.presentAlert({
        header: 'Tidak dapat mengakses lokasi',
        message:
          'Untuk menggunakan fitur ini, ubah izin lokasi pada Setting > App > TopDepo > Permission > Lokasi > Izinkan Setiap Saat',
        buttons: [
          {
            text: 'Ubah Setting',
            handler: () => {
              this.openAppSettings();
            },
          },
          'Batal',
        ],
      });

      await this.controllerService.dismissLoading();
    }
  }

  async chooseOnMap() {
    let location: Location = this.defaultLocation;

    if (this.parentFormGroup?.value?.full_address) {
      try {
        const updatedLocation = await this.updateLocation(this.parentFormGroup.value.full_address);

        if (!!updatedLocation) {
          location = updatedLocation;
        }
      } catch (error) {
        console.error('Error updating location:', error);
      }
    }

    if (this.location) {
      location.latitude = this.location.latitude;
      location.longitude = this.location.longitude;
      location.googleMapsAddress = this.location.googleMapsAddress;
      location.googleMapsSearchUrl = this.location.googleMapsSearchUrl;
    }

    const modal = await this.modalController.create({
      component: ModalLocationPickerComponent,
      componentProps: {
        location,
      },
      // backdropDismiss: true,
      // showBackdrop: true
    });

    await modal.present();

    // const result = await modal.onDidDismiss<{ latitude: number; longitude: number }>();
    const result = await modal.onDidDismiss();

    if (!result.data) {
      return;
    }

    this.location = result.data;
    // console.log("location picker map", this.location)

    this.locationChange.emit(this.location);
    // this.createLocation(result.data.latitude, result.data.longitude);
  }

  private takeDetailAddress(fullAddress: string) {
    return fullAddress.toLowerCase().split(', kec')[0];
  }

  private createLocation(latitude: number, longitude: number) {
    const location: Location = {
      latitude,
      longitude,
      staticMapUrl: this.googleMapsService.getStaticMapUrl(latitude, longitude),
    };

    this.location = location;

    this.locationChange.emit(this.location);
  }

  private async presentGeolocationErrorAlert() {
    await this.controllerService.presentAlert(
      {
        header: 'Error Geolocation',
        message: 'Gagal mengambil lokasi saat ini, harap pilih di peta!',
        buttons: ['OK'],
      },
      true,
    );
  }
}
